import { Button, Collapse, Descriptions, Space, Spin, Tag, Typography } from "antd";
import { CheckOutlined, DeleteOutlined, EditOutlined, PrinterOutlined } from "@ant-design/icons";
import { AttachmentPanel } from "../../shared/components/AttachmentPanel";
import { formatInr } from "../../shared/utils/currency";
import { formatDate } from "../../shared/utils/date";

export function DailyEntryDetailsCanvas({
  rows,
  loading,
  error,
  canEdit = false,
  canDelete = false,
  canMarkPaid = false,
  onEditRow,
  onDeleteRow,
  onMarkGroupPaid,
  onPrintGroup
}: {
  rows: any[];
  loading?: boolean;
  error?: string | null;
  canEdit?: boolean;
  canDelete?: boolean;
  canMarkPaid?: boolean;
  onEditRow?: (row: any) => void;
  onDeleteRow?: (row: any) => void;
  onMarkGroupPaid?: () => void;
  onPrintGroup?: () => void;
}) {
  if (loading) {
    return (
      <div className="daily-detail-loading">
        <Spin tip="Loading daily entry details..." />
      </div>
    );
  }
  if (error) return <div className="inline-error">{error}</div>;
  if (!rows.length) return <div className="details-empty">No entries found.</div>;
  return (
    <div className="daily-detail-list">
      <div className="daily-detail-toolbar">
        <Space wrap>
          {canMarkPaid ? <Button icon={<CheckOutlined />} onClick={onMarkGroupPaid}>Mark All Paid</Button> : null}
          <Button icon={<PrinterOutlined />} onClick={onPrintGroup}>View & Print</Button>
        </Space>
      </div>
      {rows.map((row, index) => (
        (() => {
          const status = normalizePaymentStatus(row.paymentStatus);
          const paymentType = normalizePaymentType(row.paymentTypeName ?? row.paymentType);
          const transactionType = normalizeTransactionType(row.transactionTypeName ?? row.transactionType);
          return (
        <div className="daily-detail-card" key={row.id ?? `${row.paidOfId}-${row.paymentEntryDate}-${index}`}>
          <Collapse
            ghost
            defaultActiveKey={[]}
            items={[{
              key: "details",
              label: (
                <div className="daily-detail-summary">
                  <div className="daily-detail-summary-main">
                    <span className="daily-detail-index">{index + 1}.</span>
                    <div className="daily-detail-summary-text">
                      <Typography.Text strong>{row.paidOfName || row.paymentOfName || "Daily Entry"}</Typography.Text>
                      <div className="daily-detail-sub">
                        {formatDate(row.paymentEntryDate)} for {row.shortRemarkName || row.longRemarks || "-"}
                      </div>
                    </div>
                  </div>
                  <div className="daily-detail-summary-side">
                    <Typography.Text strong className="daily-detail-amount">{formatInr(row.amount)}</Typography.Text>
                    <Space size={6} className="daily-detail-tags">
                      <Tag color={status === "Paid" ? "green" : status === "Pending" ? "gold" : "red"}>{status}</Tag>
                      <Tag color={paymentType === "Bank" ? "blue" : "green"}>{paymentType}</Tag>
                      <Tag color={transactionType === "Credit" ? "blue" : "red"}>{transactionType}</Tag>
                    </Space>
                    <div className="daily-detail-actions">
                      {canEdit && row.id ? (
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            onEditRow?.(row);
                          }}
                        >
                          Edit
                        </Button>
                      ) : null}
                      {canDelete && row.id ? (
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            onDeleteRow?.(row);
                          }}
                        >
                          Delete
                        </Button>
                      ) : null}
                      <div className="daily-detail-person">
                        <span>Entered By</span>
                        <strong>{row.enteredByName || row.enteredBy || "-"}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              ),
              children: (
                <div className="daily-detail-body">
                  <Descriptions size="small" column={{ xxl: 4, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }} bordered>
                    <Descriptions.Item label="Paid Of">{row.paidOfName || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Payment Of">{row.paymentOfName || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Bank">{row.bankName || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Account No">{row.bankAccountNumber || "-"}</Descriptions.Item>
                    <Descriptions.Item label="IFSC">{row.ifscCode || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Payment Type">{paymentType}</Descriptions.Item>
                    <Descriptions.Item label="Payment Mode">{row.paymentMode || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Reference No">{row.paymentReference || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Transaction Type">{transactionType}</Descriptions.Item>
                    <Descriptions.Item label="Status">{status}</Descriptions.Item>
                    <Descriptions.Item label="Entry Category">{row.entryCategoryName || row.accountLedgerName || row.expenseTypeName || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Expense Month">{row.expenseMonthPayment || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Entry Date">{formatDate(row.paymentEntryDate)}</Descriptions.Item>
                    <Descriptions.Item label="Paid Date">{formatDate(row.paymentPaidDate)}</Descriptions.Item>
                    <Descriptions.Item label="Short Remarks">{row.shortRemarkName || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Long Remarks" span={2}>{row.longRemarks || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Bills">
                      <Tag color={Number(row.billAttachmentCount ?? 0) > 0 ? "blue" : "default"}>{row.billAttachmentCount ?? 0} files</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Vouchers">
                      <Tag color={Number(row.voucherAttachmentCount ?? 0) > 0 ? "green" : "default"}>{row.voucherAttachmentCount ?? 0} files</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created At">{formatDateTime(row.createdAt)}</Descriptions.Item>
                    <Descriptions.Item label="Updated At">{formatDateTime(row.updatedAt)}</Descriptions.Item>
                  </Descriptions>
                  {row.id ? (
                    <div className="drawer-attachment-block">
                      <AttachmentPanel module="DailyEntry" entityId={row.id} compact />
                    </div>
                  ) : null}
                </div>
              )
            }]}
          />
        </div>
          );
        })()
      ))}
    </div>
  );
}

export function EntryDetailsCanvas({
  moduleKey,
  record,
  loading,
  error
}: {
  moduleKey: string;
  record: Record<string, unknown> | null;
  loading?: boolean;
  error?: string | null;
}) {
  if (loading) {
    return (
      <div className="daily-detail-loading">
        <Spin tip="Loading entry details..." />
      </div>
    );
  }
  if (error) return <div className="inline-error">{error}</div>;
  if (!record) return <div className="details-empty">No entry selected.</div>;

  return (
    <div className="entry-detail-panel">
      {moduleKey === "CashEntry" ? <CashEntryDetails record={record} /> : <StatementEntryDetails record={record} />}
    </div>
  );
}

function CashEntryDetails({ record }: { record: Record<string, unknown> }) {
  return (
    <>
      <DetailSection
        title="Entry"
        items={[
          ["Entry Date", formatRecordDate(record, ["dated", "Dated"])],
          ["Paid Of", readRecord(record, ["paidOf", "PaidOf"])],
          ["Account (Cash/Bank)", readRecord(record, ["paymentOf", "PaymentOf"])]
        ]}
      />
      <DetailSection
        title="Amounts"
        items={[
          ["Debit", formatInr(Number(readRecord(record, ["debit", "Debit"]) ?? 0))],
          ["Credit", formatInr(Number(readRecord(record, ["credit", "Credit"]) ?? 0))],
          ["Balance", formatInr(Number(readRecord(record, ["balance", "Balance"]) ?? 0))]
        ]}
      />
      <DetailSection title="Notes" items={[["Remarks", readRecord(record, ["remarks", "Remarks"])]]} />
      <DetailSection
        title="Attachments"
        items={[
          ["Bill Attachments", `${readRecord(record, ["billAttachmentCount", "BillAttachmentCount"]) ?? 0}`],
          ["Voucher Attachments", `${readRecord(record, ["voucherAttachmentCount", "VoucherAttachmentCount"]) ?? 0}`]
        ]}
      />
      <DetailSection
        title="Audit"
        items={[
          ["Entered By", readRecord(record, ["enteredByName", "EnteredByName", "enteredBy", "EnteredBy"])],
          ["Created At", formatDateTime(String(readRecord(record, ["createdAt", "CreatedAt"]) ?? ""))],
          ["Updated At", formatDateTime(String(readRecord(record, ["updatedAt", "UpdatedAt"]) ?? ""))]
        ]}
      />
    </>
  );
}

function StatementEntryDetails({ record }: { record: Record<string, unknown> }) {
  const isBankStatement = !!readRecord(record, ["sourceModule", "SourceModule", "voucherType", "VoucherType", "entryDate", "EntryDate"]);
  if (isBankStatement) {
    return (
      <>
        <DetailSection
          title="Transaction"
          items={[
            ["Date", formatRecordDate(record, ["entryDate", "EntryDate", "tranDate", "TranDate"])],
            ["Party", readRecord(record, ["partyName", "PartyName", "party", "Party"])],
            ["Source", readRecord(record, ["sourceModule", "SourceModule"])],
            ["Voucher", readRecord(record, ["voucherType", "VoucherType"])],
            ["Reference", readRecord(record, ["referenceNo", "ReferenceNo"])],
            ["Particulars", readRecord(record, ["particulars", "Particulars"])]
          ]}
        />
        <DetailSection
          title="Amounts"
          items={[
            ["Received", formatInr(Number(readRecord(record, ["debit", "Debit"]) ?? 0))],
            ["Paid", formatInr(Number(readRecord(record, ["credit", "Credit"]) ?? 0))],
            ["Balance", formatInr(Number(readRecord(record, ["balance", "Balance"]) ?? 0))]
          ]}
        />
      </>
    );
  }

  return (
    <>
      <DetailSection
        title="Entry"
        items={[
          ["Transaction Date", formatRecordDate(record, ["tranDate", "TranDate"])],
          ["Account (Cash/Bank)", readRecord(record, ["chequeNo", "ChequeNo"])],
          ["Particulars", readRecord(record, ["particulars", "Particulars"])],
          ["Type", readRecord(record, ["type", "Type"])]
        ]}
      />
      <DetailSection
        title="Amounts"
        items={[
          ["Debit", formatInr(Number(readRecord(record, ["debit", "Debit"]) ?? 0))],
          ["Credit", formatInr(Number(readRecord(record, ["credit", "Credit"]) ?? 0))],
          ["Balance", formatInr(Number(readRecord(record, ["balance", "Balance"]) ?? 0))]
        ]}
      />
      <DetailSection title="Notes" items={[["Narration", readRecord(record, ["narration", "Narration"])]]} />
      <DetailSection
        title="Audit"
        items={[
          ["Entered By", readRecord(record, ["enteredByName", "EnteredByName", "enteredBy", "EnteredBy"])],
          ["Created At", formatDateTime(String(readRecord(record, ["createdAt", "CreatedAt"]) ?? ""))],
          ["Updated At", formatDateTime(String(readRecord(record, ["updatedAt", "UpdatedAt"]) ?? ""))]
        ]}
      />
    </>
  );
}

function DetailSection({ title, items }: { title: string; items: Array<[string, unknown]> }) {
  return (
    <div className="entry-detail-section">
      <Typography.Text type="secondary" className="entry-detail-section-title">{title}</Typography.Text>
      <Descriptions size="small" bordered column={1}>
        {items.map(([label, value]) => (
          <Descriptions.Item key={label} label={label}>
            {value === undefined || value === null || value === "" ? "-" : String(value)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </div>
  );
}

function readRecord(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
}

function formatRecordDate(record: Record<string, unknown>, keys: string[]) {
  const value = readRecord(record, keys);
  return value ? formatDate(String(value)) : "-";
}

function formatDateTime(value?: string) {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
}

function normalizePaymentStatus(value: unknown) {
  if (value === 2 || value === "2" || value === "Paid") return "Paid";
  if (value === 3 || value === "3" || value === "Failed") return "Failed";
  if (value === 4 || value === "4" || value === "Cancelled") return "Cancelled";
  return "Pending";
}

function normalizePaymentType(value: unknown) {
  if (value === 2 || value === "2" || value === "Bank") return "Bank";
  return "Cash";
}

function normalizeTransactionType(value: unknown) {
  if (value === 2 || value === "2" || value === "Credit") return "Credit";
  return "Debit";
}
